import { useRef, useState, type KeyboardEvent } from 'react';
import { Search, X } from 'lucide-react';
import { allGames, gameCategories, type GameConfig } from '../../data/games';
import { type Badge } from '../../store/gameStore';
import DailyMatchCard from '../DailyMatchCard';
import HomeGameCard from './HomeGameCard';
import HomeCategoryDetail from './HomeCategoryDetail';
import SmartRecommendPanel from './SmartRecommendPanel';

const tabs = [
  { id: 'recommended', label: '智能推荐' },
  { id: 'free', label: '自由训练' },
  { id: 'challenge', label: '精进挑战' },
] as const;
type Tab = typeof tabs[number]['id'];

const precisionGames = [
  { mechanic: 'reaction', name: '极限反应', text: '辨信号 · 抗干扰' },
  { mechanic: 'search', name: '精准搜索', text: '多条件 · 找不同' },
  { mechanic: 'tracking', name: '稳定追踪', text: '控轨迹 · 连续锁定' },
].map(item => ({ ...item, game: allGames.filter(g => g.mechanic === item.mechanic).sort((a, b) => a.unlockEnergy - b.unlockEnergy)[0] }));
const memoryGames = [
  { id: 'me-02', name: '序列记忆', text: '动态跨度 · 顺逆重现' },
  { id: 'me-03', name: '位置记忆', text: '空间编码 · 镜像规则' },
  { id: 'me-18', name: '工作记忆', text: '持续更新 · 抗干扰' },
  { id: 'sp-07', name: '迷宫寻路', text: '地标规划 · 记忆迷雾' },
];
const recommendations = gameCategories.flatMap(c => c.games).filter(g => g.unlockEnergy === 0).slice(0, 6);

interface Props {
  energy: number;
  totalEnergy: number;
  totalGames: number;
  badges: Badge[];
  completedLevels: string[];
  activeCategory: string | null;
  onCategoryChange: (id: string | null) => void;
  onGameClick: (game: GameConfig) => void;
  searchQuery: string;
  onSearchChange: (query: string) => void;
  filteredGames: GameConfig[] | null;
}

export default function HomeDashboard({ energy, totalEnergy, totalGames, badges, completedLevels, activeCategory, onCategoryChange, onGameClick, searchQuery, onSearchChange, filteredGames }: Props) {
  const [tab, setTab] = useState<Tab>('recommended');
  const tabsRef = useRef<HTMLDivElement>(null);
  const selectTab = (id: Tab) => {
    setTab(id);
    onSearchChange('');
  };
  const handleTabKey = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
    const next = event.key === 'ArrowRight' ? (index + 1) % tabs.length
      : event.key === 'ArrowLeft' ? (index + tabs.length - 1) % tabs.length
      : event.key === 'Home' ? 0 : event.key === 'End' ? tabs.length - 1 : null;
    if (next === null) return;
    event.preventDefault();
    selectTab(tabs[next].id);
    tabsRef.current?.querySelectorAll<HTMLButtonElement>('[role="tab"]')[next]?.focus();
  };
  const category = gameCategories.find(c => c.id === activeCategory);

  return <>
    <section className="cyber-hero">
      <img src="https://media.base44.com/images/public/6aa26f45ee0e9f5ab4e67bd3/08ca3be9e_generated_2d0078db.jpg" alt="" />
      <div className="cyber-scrim" />
      <div className="cyber-orbit" aria-hidden="true" />
      <div className="cyber-hero-copy">
        <div className="cyber-eyebrow">VISION / READY</div>
        <h1>科学训练原则</h1>
        <p>每次 8-12 分钟，优先准确率，再逐步提速。</p>
        <button onClick={() => { onCategoryChange(null); selectTab('free'); tabsRef.current?.scrollIntoView({ block: 'start', behavior: 'smooth' }); }}>开始训练</button>
      </div>
    </section>
    <div className="cyber-tabs" role="tablist" aria-label="首页分区" ref={tabsRef}>
      {tabs.map((item, index) => <button key={item.id} id={`home-tab-${item.id}`} role="tab" aria-selected={tab === item.id} aria-controls="home-tabpanel" tabIndex={tab === item.id ? 0 : -1} className={`cyber-tab ${tab === item.id ? 'active' : ''}`} onClick={() => selectTab(item.id)} onKeyDown={event => handleTabKey(event, index)}>{item.label}</button>)}
    </div>
    <div className="cyber-search">
      <Search size={14} aria-hidden="true" />
      <input aria-label="搜索训练游戏" placeholder={`搜索${totalGames}款训练游戏...`} value={searchQuery} onChange={event => onSearchChange(event.target.value)} />
      {searchQuery && <button className="cyber-icon" aria-label="清除搜索" onClick={() => onSearchChange('')}><X size={14} /></button>}
    </div>
    <main id="home-tabpanel" role="tabpanel" aria-labelledby={`home-tab-${tab}`} className="cyber-main">
      {filteredGames ? <section className="cyber-panel cyber-wide">
        <div className="cyber-panel-head"><h2 className="cyber-panel-title" role="status">找到 {filteredGames.length} 款游戏</h2></div>
        <div className="cyber-games cyber-catalog-games">{filteredGames.slice(0, 20).map(game => <HomeGameCard key={game.id} game={game} onClick={() => onGameClick(game)} completed={completedLevels.includes(game.id)} />)}</div>
      </section> : <>
        {tab === 'recommended' && <>
          <SmartRecommendPanel completedLevels={completedLevels} onGameClick={onGameClick} />
          <section className="cyber-panel">
            <div className="cyber-panel-head"><h2 className="cyber-panel-title">训练进度</h2><span className="cyber-panel-meta">{completedLevels.length} / {totalGames}</span></div>
            <div className="cyber-principles">
              <div className="cyber-principle"><strong>科学训练原则</strong><span>每次 8-12 分钟，优先准确率，再逐步提速。</span></div>
              <div className="cyber-principle"><strong>训练质量标准</strong><span>连续两轮达标后再升难度，保证稳定进步。</span></div>
            </div>
            <div className="cyber-panel-meta cyber-progress-total">已获得 {badges.length} 枚勋章 · 累计 {totalEnergy} 能量</div>
          </section>
          <DailyMatchCard terminal />
          {badges.length > 0 && <section className="cyber-panel cyber-wide"><div className="cyber-panel-head"><h2 className="cyber-panel-title">成就勋章</h2></div><div className="cyber-badges">{badges.map(badge => <span key={badge.id}>{badge.icon} {badge.name}</span>)}</div></section>}
        </>}
        {tab === 'free' && <>
          <section className="cyber-panel cyber-wide">
            <div className="cyber-panel-head"><h2 className="cyber-panel-title">自由训练</h2><span className="cyber-panel-meta">{gameCategories.length} 个分类 · {totalGames} 款游戏</span></div>
            <strong>想练哪个，就练哪个</strong>
            <p className="cyber-description">不依赖问卷与推荐，直接按分类或搜索挑选训练项目。</p>
          </section>
          {category ? <HomeCategoryDetail key={category.id} category={category} completedLevels={completedLevels} onBack={() => onCategoryChange(null)} onGameClick={onGameClick} /> : <>
            <section className="cyber-panel cyber-wide">
              <div className="cyber-panel-head"><h2 className="cyber-panel-title">训练分类</h2><span className="cyber-panel-meta">{gameCategories.length} 个分类</span></div>
              <div className="cyber-categories">{gameCategories.map(cat => <button key={cat.id} className="cyber-category" onClick={() => onCategoryChange(cat.id)} title={cat.description}>{cat.name}<em>{cat.games.length}款游戏<span>{cat.games.filter(g => completedLevels.includes(g.id)).length}/{cat.games.length}</span></em></button>)}</div>
            </section>
            <section className="cyber-panel cyber-wide">
              <div className="cyber-panel-head"><h2 className="cyber-panel-title">零门槛入门</h2><span className="cyber-panel-meta">无需能量解锁</span></div>
              <div className="cyber-games cyber-recommended-games">{recommendations.map(game => <HomeGameCard key={game.id} game={game} onClick={() => onGameClick(game)} completed={completedLevels.includes(game.id)} compact />)}</div>
            </section>
          </>}
        </>}
        {tab === 'challenge' && <>
          <section className="cyber-panel">
            <div className="cyber-panel-head"><h2 className="cyber-panel-title">精进挑战馆</h2><span className="cyber-panel-meta">玩法升级</span></div>
            <strong>从准确，到从容。</strong>
            <p className="cyber-description">三档进阶难度，挑战自己的最佳表现。</p>
            <div className="cyber-challenge-grid">{precisionGames.map(({ name, text, game }) => game && <button key={name} className="cyber-challenge" onClick={() => onGameClick(game)}><b>{name}</b><span>{text}</span></button>)}</div>
          </section>
          <section className="cyber-panel">
            <div className="cyber-panel-head"><h2 className="cyber-panel-title">新一轮精进</h2></div>
            <strong>记住每一步，走出新路线</strong>
            <p className="cyber-description">序列重现 · 工作记忆 · 迷雾寻路</p>
            <button className="cyber-challenge cyber-match-feature" onClick={() => onGameClick(allGames.find(g => g.id === 'me-01')!)}><b>翻牌配对 · 全新进阶</b><span>记住位置 · 辨认相似 · 一实配一空</span></button>
            <div className="cyber-challenge-grid">{memoryGames.map(({ id, name, text }) => {
              const game = allGames.find(g => g.id === id)!;
              return <button key={id} className="cyber-challenge" onClick={() => onGameClick(game)}><b>{name}</b><span>{text}</span><small>{game.unlockEnergy > energy ? `${game.unlockEnergy} 能量解锁` : '开始探索 →'}</small></button>;
            })}</div>
          </section>
        </>}
      </>}
    </main>
  </>;
}
